import {encrypt} from "../../../utils/crypto"

// Jeu de données de test pour les API externes (/api/external/v1, voir
// network/externalClientAPI.js et network/externalAnimalsAPI.js).
// Chaque document créé porte `mockData: true` : le job supprime d'abord le jeu
// précédent, le relancer réinitialise donc les données mock sans doublon.
// Les codes sont préfixés `mock_` pour ne pas entrer en collision avec les
// référentiels réels (contraintes d'unicité sur code / name).

const MOCK_FLAG = {mockData: true}

const MOCK_ENTITIES = ['Animal', 'AnimalCompatibilityEvaluation', 'Shelter', 'ShelterOpeningHours', 'CUser', 'Organization', 'Race', 'AgeInterval', 'AnimalType', 'Compatibility', 'Character', 'Handicap']

const AGE_INTERVALS_BY_ANIMAL_TYPE = {
    mock_chien: [[0, 1, 'junior'], [1, 3, 'adult'], [3, 7, 'adult'], [7, null, 'senior']],
    mock_chat: [[0, 1, 'junior'], [1, 3, 'adult'], [3, 7, 'adult'], [7, null, 'senior']],
    mock_nac: [[0, 1, 'junior'], [1, 5, 'adult'], [5, null, 'senior']]
}

const ANIMAL_TYPES = [
    {code: 'mock_chien', name: 'Chien (mock)', animalTypeGroup: 'domesticCarnivores'},
    {code: 'mock_chat', name: 'Chat (mock)', animalTypeGroup: 'domesticCarnivores'},
    {code: 'mock_nac', name: 'NAC (mock)', animalTypeGroup: 'wildlife'}
]

const RACES = [
    {code: 'mock_labrador', name: 'Labrador (mock)', animalType: 'mock_chien', isCategorizedRace: false},
    {code: 'mock_staffordshire', name: 'American Staffordshire (mock)', animalType: 'mock_chien', isCategorizedRace: true,
        adoptionConditions: "Chien de 2e catégorie : permis de détention et assurance responsabilité civile obligatoires."},
    {code: 'mock_europeen', name: 'Européen (mock)', animalType: 'mock_chat', isCategorizedRace: false},
    {code: 'mock_maine_coon', name: 'Maine Coon (mock)', animalType: 'mock_chat', isCategorizedRace: false},
    {code: 'mock_lapin_nain', name: 'Lapin nain (mock)', animalType: 'mock_nac', isCategorizedRace: false}
]

const COMPATIBILITIES = [
    {code: 'mock_enfants', name: 'Enfants (mock)', type: 'human'},
    {code: 'mock_chats', name: 'Chats (mock)', type: 'animal'},
    {code: 'mock_chiens', name: 'Chiens (mock)', type: 'animal'},
    {code: 'mock_appartement', name: 'Appartement (mock)', type: 'place'},
    {code: 'mock_jardin', name: 'Maison avec jardin (mock)', type: 'place'}
]

const CHARACTERS = [
    {code: 'mock_calme', name: 'Calme (mock)'},
    {code: 'mock_joueur', name: 'Joueur (mock)'},
    {code: 'mock_affectueux', name: 'Affectueux (mock)'}
]

const HANDICAPS = [
    {code: 'mock_aveugle', name: 'Aveugle (mock)'},
    {code: 'mock_tripode', name: 'Tripode (mock)'}
]

const CONTACTS = [
    {key: 'contact1', civility: 'mme', firstname: 'Claire', lastname: 'Mock', mail: 'claire.mock@example.org', phone: '+33601020304', landline: null},
    // Seulement un téléphone fixe : il doit être renvoyé dans establishmentContact.landline.
    {key: 'contact2', civility: 'm', firstname: 'Paul', lastname: 'Mock', mail: 'paul.mock@example.org', phone: null, landline: '+33478000000'}
]

const SHELTERS = [
    {
        key: 'paris', code: '99999999900011', organization: '999999999', name: '[MOCK] Refuge de Paris', status: 'headquarters', publishOnWebsite: true,
        types: ['adoption', 'pound'], animalTypes: ['mock_chien', 'mock_chat'],
        address: '10 rue de la Paix', zipCode: '75002', city: 'Paris', lat: 48.8686, lng: 2.3314,
        shelterSchedules: 'Du lundi au samedi, 9h-12h / 14h-18h',
        openingHours: [
            [['Monday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], '10:00', '12:00'],
            [['Monday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], '14:00', '18:00']
        ],
        contact: 'contact1'
    },
    {
        key: 'lyon', code: '99999999900029', organization: '999999999', name: '[MOCK] Refuge de Lyon', status: 'secondary', publishOnWebsite: true,
        types: ['adoption', 'hostFamily'], animalTypes: ['mock_chat', 'mock_nac'],
        address: '5 quai Saint-Antoine', zipCode: '69002', city: 'Lyon', lat: 45.7640, lng: 4.8357,
        shelterSchedules: 'Du mardi au dimanche, 10h-18h',
        openingHours: [[['Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], '10:00', '18:00']],
        contact: 'contact2'
    },
    {
        key: 'ajaccio', code: '99999999900037', organization: '999999999', name: '[MOCK] Refuge d’Ajaccio', status: 'secondary', publishOnWebsite: true,
        types: ['adoption', 'retirement'], animalTypes: ['mock_chien', 'mock_chat', 'mock_nac'],
        address: '1 cours Napoléon', zipCode: '20000', city: 'Ajaccio', lat: 41.9192, lng: 8.7386,
        shelterSchedules: 'Du lundi au vendredi, 8h-16h',
        contact: null
    },
    {
        // Non publié : ne doit jamais apparaître dans l'API.
        key: 'cayenne', code: '99999999900045', organization: '999999999', name: '[MOCK] Fourrière de Cayenne (non publiée)', status: 'secondary', publishOnWebsite: false,
        types: ['pound'], animalTypes: ['mock_chien'],
        address: '2 avenue du Général de Gaulle', zipCode: '97300', city: 'Cayenne', lat: 4.9372, lng: -52.3260,
        shelterSchedules: 'Du lundi au vendredi, 9h-17h',
        contact: null
    },
    {
        // Publié mais fermé : ni l'établissement ni ses animaux ne doivent apparaître dans l'API animaux.
        key: 'nantes', code: '99999999900052', organization: '999999999', name: '[MOCK] Refuge de Nantes (fermé)', status: 'closed', publishOnWebsite: true,
        types: ['adoption'], animalTypes: ['mock_chien', 'mock_chat'],
        address: '3 place Royale', zipCode: '44000', city: 'Nantes', lat: 47.2138, lng: -1.5603,
        shelterSchedules: 'Fermé',
        contact: null
    },
    {
        // Publié, mais son association est suspendue : ses animaux ne doivent pas apparaître dans l'API.
        key: 'lille', code: '99999999800013', organization: '999999998', name: '[MOCK] Refuge de Lille (association suspendue)', status: 'headquarters', publishOnWebsite: true,
        types: ['adoption'], animalTypes: ['mock_chien', 'mock_chat'],
        address: '8 rue Esquermoise', zipCode: '59000', city: 'Lille', lat: 50.6372, lng: 3.0610,
        shelterSchedules: 'Du lundi au samedi, 10h-17h',
        contact: null
    }
]

// Associations : la seconde est suspendue (ses animaux sont masqués de l'API).
const ORGANIZATIONS = [
    {code: '999999999', name: '[MOCK] Association de test API', status: 'member', address: '10 rue de la Paix', zipCode: '75002', city: 'Paris'},
    {code: '999999998', name: '[MOCK] Association suspendue', status: 'suspended', address: '8 rue Esquermoise', zipCode: '59000', city: 'Lille'}
]

// [name, shelter, animalType, race, sex, size, minAge, home, compatibilities, solitude, characters, handicaps, suitablePlaces, hasAdoptionFee, adoptionStatus]
// compatibilities : évaluation par cible ('1' Oui, '2' Non, '3' Non évaluée) ;
// une cible absente est créée en « Non évaluée », comme en saisie.
const COMPATIBILITY_TARGETS = ['mock_enfants', 'mock_chats', 'mock_chiens']

const ANIMALS = [
    ['Rex', 'paris', 'mock_chien', 'mock_labrador', '2', '3', 0, '1', {mock_enfants: '1', mock_chats: '2', mock_chiens: '1'}, '1', ['mock_joueur'], [], ['mock_jardin'], 'yes', 'toBePresented'],
    ['Bella', 'paris', 'mock_chien', 'mock_labrador', '1', '2', 1, '2', {mock_enfants: '1', mock_chats: '1', mock_chiens: '1'}, '2', ['mock_affectueux', 'mock_calme'], [], ['mock_appartement', 'mock_jardin'], 'no', 'toBePresented'],
    ['Tyson', 'paris', 'mock_chien', 'mock_staffordshire', '2', '3', 3, '1', {mock_enfants: '2', mock_chats: '2', mock_chiens: '1'}, '3', ['mock_calme'], [], ['mock_jardin'], 'yes', 'toBePresented'],
    ['Vieux Max', 'ajaccio', 'mock_chien', 'mock_labrador', '2', '2', 7, '1', {mock_enfants: '2', mock_chats: '2', mock_chiens: '2'}, '1', ['mock_calme'], ['mock_tripode'], ['mock_jardin'], 'no', 'toBePresented'],
    ['Nala', 'paris', 'mock_chat', 'mock_europeen', '1', '1', 0, '2', {mock_enfants: '1', mock_chats: '1'}, '1', ['mock_joueur', 'mock_affectueux'], [], ['mock_appartement'], 'yes', 'toBePresented'],
    ['Simba', 'lyon', 'mock_chat', 'mock_maine_coon', '2', '2', 1, '1', {}, '3', [], [], [], 'yes', 'toBePresented'],
    ['Mistigri', 'lyon', 'mock_chat', 'mock_europeen', '2', '1', 3, '2', {mock_enfants: '1', mock_chiens: '2'}, '1', ['mock_calme'], ['mock_aveugle'], ['mock_appartement'], 'no', 'toBePresented'],
    ['Grisette', 'ajaccio', 'mock_chat', 'mock_europeen', '1', '1', 7, '1', {mock_chats: '1', mock_chiens: '1'}, '1', ['mock_calme', 'mock_affectueux'], [], ['mock_appartement', 'mock_jardin'], 'yes', 'toBePresented'],
    ['Panpan', 'lyon', 'mock_nac', 'mock_lapin_nain', '2', '1', 0, '2', {mock_enfants: '1'}, '1', ['mock_joueur'], [], ['mock_appartement'], 'no', 'toBePresented'],
    ['Noisette', 'lyon', 'mock_nac', 'mock_lapin_nain', '1', '1', 1, '1', {mock_enfants: '2', mock_chats: '2', mock_chiens: '2'}, '2', [], [], ['mock_jardin'], 'yes', 'toBePresented'],
    ['Pépère', 'ajaccio', 'mock_nac', 'mock_lapin_nain', '2', '1', 5, '1', {}, '3', ['mock_calme'], ['mock_aveugle'], [], 'no', 'toBePresented'],
    ['Oscar', 'ajaccio', 'mock_chien', 'mock_staffordshire', '2', '2', 1, '2', {mock_enfants: '1', mock_chiens: '1'}, '1', ['mock_affectueux'], [], ['mock_jardin'], 'yes', 'toBePresented'],
    // Adoptés : ne doivent jamais apparaître dans l'API.
    ['Adopté Filou', 'paris', 'mock_chat', 'mock_europeen', '2', '1', 1, '1', {mock_enfants: '1'}, '1', [], [], [], 'yes', 'adopted'],
    ['Adoptée Lola', 'lyon', 'mock_chien', 'mock_labrador', '1', '2', 3, '1', {mock_chats: '1'}, '1', [], [], [], 'no', 'adopted'],
    // À présenter, mais refuge non publié / fermé / association suspendue : ne doivent jamais apparaître dans l'API.
    ['Lucky', 'cayenne', 'mock_chien', 'mock_labrador', '2', '2', 1, '1', {mock_enfants: '1'}, '1', [], [], [], 'no', 'toBePresented'],
    ['Caramel', 'nantes', 'mock_chat', 'mock_europeen', '1', '1', 1, '1', {mock_enfants: '1'}, '1', [], [], [], 'no', 'toBePresented'],
    ['Pixel', 'lille', 'mock_chien', 'mock_labrador', '2', '2', 3, '1', {mock_enfants: '1'}, '1', [], [], [], 'no', 'toBePresented']
]

// Informations particulières (modalités d'adoption) renseignées sur certains animaux.
const SPECIFIC_INFORMATION = {
    Rex: "Nous pouvons transporter l'animal jusqu'à chez vous si vous êtes en région Île-de-France (donnée de test)."
}

const ANIMAL_DESCRIPTION = name => `${name} est un animal mock créé pour tester les API externes.`
const ANIMAL_HISTORY = name => `${name} a été confié au refuge à la suite d’un abandon (donnée de test).`

async function getCndaGroupId() {
    const groupModel = await global.GroupModel.get({model: 'C'}, {fieldPath: ['id', 'group.id']})
    if (!groupModel || !groupModel.group) throw new Error("GroupModel CNDA introuvable")
    return new global.ObjectID(groupModel.group.id)
}

function collectionOf(entityName) {
    return global.app.C[entityName].collection
}

async function deletePreviousMockData() {
    await Promise.all(MOCK_ENTITIES.map(entityName => collectionOf(entityName).deleteMany(MOCK_FLAG)))
}

// Insère les documents (avec `group` + flag mock) et renvoie un index code/key -> _id.
async function insertAll(entityName, documents, group, keyOf = doc => doc.code) {
    const withIds = documents.map(doc => ({_id: new global.ObjectID(), ...doc, group, ...MOCK_FLAG}))
    if (withIds.length) await collectionOf(entityName).insertMany(withIds)
    return new Map(withIds.map((doc, index) => [keyOf(documents[index], index), doc._id]))
}

function idsOf(index, keys) {
    return keys.map(key => index.get(key))
}

async function applyJob() {
    const group = await getCndaGroupId()
    const now = new Date()

    await deletePreviousMockData()

    // Espèces + tranches d'âge (lien OTM stocké des deux côtés)
    const animalTypeIds = new Map(ANIMAL_TYPES.map(animalType => [animalType.code, new global.ObjectID()]))
    const ageIntervals = ANIMAL_TYPES.flatMap(animalType =>
        AGE_INTERVALS_BY_ANIMAL_TYPE[animalType.code].map(([minAge, maxAge, bracket]) => ({
            key: `${animalType.code}_${minAge}`,
            minAge,
            maxAge,
            bracket,
            animalType: animalTypeIds.get(animalType.code)
        }))
    )
    const ageIntervalIds = await insertAll('AgeInterval', ageIntervals.map(({key, ...interval}) => interval), group,
        (interval, index) => ageIntervals[index].key)
    await collectionOf('AnimalType').insertMany(ANIMAL_TYPES.map(animalType => ({
        _id: animalTypeIds.get(animalType.code),
        ...animalType,
        ageIntervals: ageIntervals
            .filter(interval => interval.animalType.equals(animalTypeIds.get(animalType.code)))
            .map(interval => ageIntervalIds.get(interval.key)),
        group,
        ...MOCK_FLAG
    })))

    // Référentiels
    const raceIds = await insertAll('Race', RACES.map(race => ({
        adoptionConditions: null,
        ...race,
        animalType: animalTypeIds.get(race.animalType)
    })), group)
    const compatibilityIds = await insertAll('Compatibility', COMPATIBILITIES, group)
    const characterIds = await insertAll('Character', CHARACTERS, group)
    const handicapIds = await insertAll('Handicap', HANDICAPS, group)

    // Organisations
    const organizationIds = await insertAll('Organization', ORGANIZATIONS.map(organization => ({
        code: organization.code,
        name: organization.name,
        tradingName: organization.name,
        organizationType: 'association',
        status: organization.status,
        // Required by the Organisation form, so that the mock organizations can be saved from the BO.
        statusDate: organization.status === 'member' ? null : now,
        rup: 'no',
        endowmentFund: false,
        member: '1',
        address: organization.address,
        zipCode: organization.zipCode,
        city: organization.city,
        country: 'France',
        createdAt: now,
        lastModificationDate: now
    })), group)
    const organizationId = organizationIds.get('999999999')

    // Contacts d'établissement (mail / téléphone chiffrés comme en saisie, téléphones au format +33 du BO)
    const contactIds = await insertAll('CUser', CONTACTS.map(contact => ({
        civility: contact.civility,
        firstname: contact.firstname,
        lastname: contact.lastname,
        mail: encrypt(contact.mail),
        phone: contact.phone ? encrypt(contact.phone) : null,
        phone2: contact.landline ? encrypt(contact.landline) : null,
        username: contact.mail,
        active: false,
        organization: organizationId,
        shelters: [],
        handledShelters: []
    })), group, (contact, index) => CONTACTS[index].key)

    // Établissements
    const shelterIds = await insertAll('Shelter', SHELTERS.map(shelter => ({
        code: shelter.code,
        name: shelter.name,
        organization: organizationIds.get(shelter.organization),
        status: shelter.status,
        publishOnWebsite: shelter.publishOnWebsite,
        types: shelter.types,
        animalTypes: idsOf(animalTypeIds, shelter.animalTypes),
        address: shelter.address,
        additionalAddress: null,
        zipCode: shelter.zipCode,
        city: shelter.city,
        country: 'France',
        lat: shelter.lat,
        lng: shelter.lng,
        shelterSchedules: shelter.shelterSchedules,
        establishmentContact: shelter.contact ? contactIds.get(shelter.contact) : null,
        syntheticPresentation: `${shelter.name} : établissement de test pour les API externes.`,
        teamAndMissions: "Une équipe de bénévoles dédiée à l'accueil et à l'adoption (donnée de test).",
        keyFigures: '120 adoptions par an (donnée de test)',
        website: 'https://example.org',
        facebookLink: 'https://facebook.com/example',
        instagramLink: 'https://instagram.com/example',
        tiktokLink: null,
        youtubeLink: null,
        linkedinLink: null,
        images: [],
        files: [],
        conversations: [],
        createdAt: now,
        lastModificationDate: now
    })), group, (shelter, index) => SHELTERS[index].key)

    // Horaires structurés (lien OTM stocké des deux côtés)
    await Promise.all(SHELTERS.filter(shelter => shelter.openingHours).map(async shelter => {
        const slots = shelter.openingHours.map(([days, opens, closes]) => ({days, opens, closes, shelter: shelterIds.get(shelter.key)}))
        const slotIds = await insertAll('ShelterOpeningHours', slots, group, (slot, index) => index)
        await collectionOf('Shelter').updateOne({_id: shelterIds.get(shelter.key)}, {$set: {openingHours: [...slotIds.values()]}})
    }))

    // Les contacts sont rattachés à tous les établissements de l'organisation
    // (liste « Utilisateurs » de la fiche) et gèrent ceux dont ils sont le contact.
    const organizationShelterIds = SHELTERS
        .filter(shelter => shelter.organization === '999999999')
        .map(shelter => shelterIds.get(shelter.key))
    await Promise.all(CONTACTS.map(contact => {
        const handled = SHELTERS.filter(shelter => shelter.contact === contact.key).map(shelter => shelterIds.get(shelter.key))
        return collectionOf('CUser').updateOne(
            {_id: contactIds.get(contact.key)},
            {$set: {shelters: organizationShelterIds, handledShelters: handled}}
        )
    }))

    const shelterOrganization = new Map(SHELTERS.map(shelter => [shelter.key, organizationIds.get(shelter.organization)]))

    // Animaux
    const animalIds = await insertAll('Animal', ANIMALS.map(([name, shelter, animalType, race, sex, size, minAge, home, compatibilities, solitude, characters, handicaps, suitablePlaces, hasAdoptionFee, adoptionStatus]) => ({
        name,
        organization: shelterOrganization.get(shelter),
        establishment: shelterIds.get(shelter),
        animalType: animalTypeIds.get(animalType),
        race: raceIds.get(race),
        sex,
        size,
        birthDate: `${now.getFullYear() - minAge}-01`,
        currentHome: home,
        compatibilityEvaluations: [],
        toleranceToSolitudeEvaluation: solitude,
        characters: idsOf(characterIds, characters),
        handicaps: idsOf(handicapIds, handicaps),
        suitablePlaces: idsOf(compatibilityIds, suitablePlaces),
        hasAdoptionFee,
        adoptionFee: hasAdoptionFee === 'yes' ? 150 : 0,
        adoptionStatus,
        description: ANIMAL_DESCRIPTION(name),
        history: ANIMAL_HISTORY(name),
        specificInformation: SPECIFIC_INFORMATION[name] || null,
        images: []
    })), group, doc => doc.name)

    // Évaluations de compatibilité par cible (lien OTM stocké des deux côtés)
    const evaluations = ANIMALS.flatMap(([name, , , , , , , , compatibilities]) =>
        COMPATIBILITY_TARGETS.map(target => ({
            compatibility: compatibilityIds.get(target),
            evaluation: compatibilities[target] || '3',
            animal: animalIds.get(name)
        }))
    )
    const evaluationIds = await insertAll('AnimalCompatibilityEvaluation', evaluations, group, (evaluation, index) => index)
    await Promise.all([...animalIds].map(([name, animalId]) => collectionOf('Animal').updateOne(
        {_id: animalId},
        {$set: {compatibilityEvaluations: evaluations
            .map((evaluation, index) => evaluation.animal.equals(animalId) ? evaluationIds.get(index) : null)
            .filter(Boolean)}}
    )))

    return {
        message: `Données mock créées (jeu précédent supprimé) : ${animalTypeIds.size} espèces, ${ageIntervalIds.size} tranches d'âge, `
            + `${raceIds.size} races, ${compatibilityIds.size} compatibilités, ${shelterIds.size} établissements, ${animalIds.size} animaux`
    }
}

export const job = {
    name: "injectExternalApiMockData",
    title: "Injecter des données mock pour les API externes",
    execute: function(context, callback) {
        applyJob()
            .then(result => callback(null, result))
            .catch(callback)
    }
}
